import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle,
  Save,
  Tag,
  Timer,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate, useParams } from 'react-router-dom';
import LanguageIcon, { detectLanguage } from '../components/LanguageIcon';
import { useAuth } from '../contexts/AuthContext';
import { formatExerciseTitle } from '../utils/formatTitle';
import { getApiUrl } from '../utils/apiUrl';

interface Exercise {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  estimated_time: number;
  github_url: string;
  tags: string[];
}

interface UserProgress {
  id: string;
  status: string;
  completed_steps: string[];
  notes: string;
}

interface Step {
  id: string;
  title: string;
  content: string;
  type: string;
  order: number;
}

// Remove HTML comments from markdown (like the metadata comments)
const removeHtmlComments = (markdown: string): string => {
  return markdown.replace(/<!--[\s\S]*?-->/g, '').trim();
};

// Extract steps from markdown content
// Looks for "Exercise*" section (H2) and extracts steps (H3) within it
const extractStepsFromMarkdown = (markdown: string): Step[] => {
  // Remove HTML comments first
  const cleanMarkdown = removeHtmlComments(markdown);

  // Find the "Exercise*" section (H2 heading)
  const exerciseSectionRegex = /^## (Exercise[^\n]*)$/gim;
  const exerciseSectionMatch = exerciseSectionRegex.exec(cleanMarkdown);

  if (!exerciseSectionMatch) {
    // Fallback: look for any H2 heading
    const anyH2Regex = /^## (.+)$/gm;
    const h2Matches = [...cleanMarkdown.matchAll(anyH2Regex)];

    if (h2Matches.length === 0) {
      return [
        {
          id: 'step-1',
          title: 'Exercise Content',
          content: cleanMarkdown.trim(),
          type: 'instruction',
          order: 1,
        },
      ];
    }

    // Use first H2 as exercise section
    const firstH2 = h2Matches[0];
    const exerciseSectionStart = firstH2.index || 0;
    const exerciseSectionEnd =
      h2Matches.length > 1
        ? h2Matches[1]?.index ?? cleanMarkdown.length
        : cleanMarkdown.length;
    const exerciseSectionContent = cleanMarkdown.slice(
      exerciseSectionStart,
      exerciseSectionEnd
    );

    // Look for H3 steps within this section
    return extractStepsFromSection(
      exerciseSectionContent,
      exerciseSectionStart
    );
  }

  const exerciseSectionStart = exerciseSectionMatch.index || 0;

  // Find where this section ends (next H2 or end of document)
  const nextH2Regex = /^## /gm;
  nextH2Regex.lastIndex = exerciseSectionStart + exerciseSectionMatch[0].length;
  const nextH2Match = nextH2Regex.exec(cleanMarkdown);
  const exerciseSectionEnd = nextH2Match
    ? nextH2Match.index
    : cleanMarkdown.length;

  const exerciseSectionContent = cleanMarkdown.slice(
    exerciseSectionStart,
    exerciseSectionEnd
  );

  // Extract steps (H3 headings) from within the exercise section
  return extractStepsFromSection(exerciseSectionContent, exerciseSectionStart);
};

// Extract steps from a section (H3 headings that match "Step X" or just "X")
const extractStepsFromSection = (
  sectionContent: string,
  sectionOffset: number
): Step[] => {
  // Match H3 headings that are either "Step 1", "Step 2" or just "1", "2", "3", etc.
  // Also match variations like "1.", "Step 1:", etc.
  const stepRegex = /^###\s+(?:Step\s+)?(\d+)[.:]?\s*(.*)$/gim;
  const stepMatches = [...sectionContent.matchAll(stepRegex)];

  if (stepMatches.length === 0) {
    // No numbered steps found, return the whole section as one step
    return [
      {
        id: 'step-1',
        title: 'Exercise Content',
        content: sectionContent.trim(),
        type: 'instruction',
        order: 1,
      },
    ];
  }

  return stepMatches.map((match, index) => {
    const stepNumber = parseInt(match[1], 10);
    const stepTitleSuffix = match[2]?.trim() || '';
    const stepTitle = stepTitleSuffix
      ? `Step ${stepNumber}: ${stepTitleSuffix}`
      : `Step ${stepNumber}`;

    // Get content from the H3 heading to the next H3 (or end)
    const stepContent = sectionContent
      .slice(
        match.index || 0,
        stepMatches[index + 1]?.index ?? sectionContent.length
      )
      .trim();

    return {
      id: `step-${stepNumber}`,
      title: stepTitle,
      content: stepContent,
      type: stepContent.includes('```') ? 'code' : 'instruction',
      order: stepNumber,
    };
  });
};

/**
 * Parse numbered items (e.g. "1. **Create a directory...**") from exercise content
 * and return them as individual steps. Used when API returns a single "Exercise: Step-by-Step"
 * step containing many numbered sub-tasks.
 */
const extractNumberedStepsFromContent = (content: string): Step[] => {
  // Match "1. **Title**" at start of line - capture number, title, and body until next match
  const regex = /(?:^|\n)(\d+)\.\s+\*\*(.+?)\*\*([\s\S]*?)(?=\n\d+\.\s+\*\*|$)/g;
  const matches: Array<{ num: number; title: string; body: string }> = [];
  let m;
  while ((m = regex.exec(content)) !== null) {
    const num = parseInt(m[1], 10);
    const title = m[2].trim();
    const body = (m[3] || '').trim();
    matches.push({ num, title, body });
  }
  if (matches.length === 0) return [];
  return matches.map(({ num, title, body }) => ({
    id: `step-${num}`,
    title,
    content: `### ${num}. ${title}\n\n${body}`.trim(),
    type: body.includes('```') ? 'code' : 'instruction',
    order: num,
  }));
};

/**
 * Transform API steps: when the main exercise step contains numbered sub-items,
 * expand them into individual completable steps. Keeps Overview/Prerequisites as
 * intro and Summary/Follow-Up as outro in the content, but only the numbered
 * items appear as steps to complete.
 */
const transformApiStepsToCompletableSteps = (
  apiSteps: Step[]
): { steps: Step[]; fullContent: string } => {
  if (!apiSteps || apiSteps.length === 0) {
    return { steps: [], fullContent: '' };
  }

  const introKeywords = ['overview', 'prerequisites', 'introduction'];
  const outroKeywords = ['summary', 'follow-up', 'follow up', 'next steps'];
  const exerciseKeywords = ['exercise', 'step-by-step', 'part 1', 'part 2'];

  let introContent = '';
  let exerciseStep: Step | null = null;
  let outroContent = '';

  for (let i = 0; i < apiSteps.length; i++) {
    const s = apiSteps[i];
    const titleLower = (s.title || '').toLowerCase();
    const isIntro = introKeywords.some(k => titleLower.includes(k));
    const isOutro = outroKeywords.some(k => titleLower.includes(k));
    const isExercise =
      exerciseKeywords.some(k => titleLower.includes(k)) ||
      (s.type === 'code' && s.content.length > 500);

    if (isIntro) {
      introContent += (introContent ? '\n\n' : '') + s.content;
    } else if (isOutro) {
      outroContent += (outroContent ? '\n\n' : '') + s.content;
    } else if (isExercise && !exerciseStep) {
      exerciseStep = s;
    } else if (!exerciseStep) {
      introContent += (introContent ? '\n\n' : '') + s.content;
    } else {
      outroContent += (outroContent ? '\n\n' : '') + s.content;
    }
  }

  if (exerciseStep) {
    const expandedSteps = extractNumberedStepsFromContent(exerciseStep.content);
    if (expandedSteps.length > 0) {
      const expandedContent = expandedSteps
        .map(s => s.content)
        .join('\n\n---\n\n');
      const fullContent = [introContent, expandedContent, outroContent]
        .filter(Boolean)
        .join('\n\n---\n\n');
      return { steps: expandedSteps, fullContent };
    }
  }

  return {
    steps: apiSteps,
    fullContent: apiSteps.map(s => s.content).join('\n\n'),
  };
};

// Celebration overlay component with animated icons
const CelebrationOverlay: React.FC = () => {
  const [icons, setIcons] = useState<
    Array<{
      id: number;
      emoji: string;
      x: number;
      y: number;
      rotation: number;
      delay: number;
    }>
  >([]);

  useEffect(() => {
    // Collection of fun celebration icons
    const celebrationIcons = [
      // Spaceships & Tech
      '🚀',
      '🛸',
      '🛰️',
      '👾',
      '🤖',
      // Animals
      '🐱',
      '🐶',
      '🐼',
      '🦄',
      '🐨',
      '🦁',
      '🐯',
      '🐸',
      '🐰',
      '🐻',
      // Stars & Sparkles
      '⭐',
      '🌟',
      '✨',
      '💫',
      '⚡',
      '🔥',
      '💎',
      // Party & Celebration
      '🎉',
      '🎊',
      '🎈',
      '🎁',
      '🎀',
      '🎪',
      '🎭',
      // Positive & Fun
      '💪',
      '🎯',
      '🏆',
      '🥇',
      '🎮',
      '🎨',
      '🎵',
      '🎸',
      // Food & Treats
      '🍕',
      '🍰',
      '🍦',
      '🍭',
      '🍪',
      '🧁',
    ];

    // Generate random icons with random positions and animations
    const newIcons = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      emoji:
        celebrationIcons[Math.floor(Math.random() * celebrationIcons.length)],
      x: Math.random() * 100, // Random horizontal position (0-100%)
      y: Math.random() * 100, // Random vertical position (0-100%)
      rotation: Math.random() * 360, // Random rotation
      delay: Math.random() * 0.5, // Staggered animation delay
    }));

    setIcons(newIcons);
  }, []);

  // Random positive messages
  const messages = [
    '🎉 Awesome!',
    '✨ Great job!',
    "🚀 You're crushing it!",
    '💪 Keep it up!',
    '🌟 Excellent!',
    '🎊 Well done!',
    '🔥 On fire!',
    '⭐ Stellar work!',
    '💯 Perfect!',
    '🎈 Fantastic!',
    '🏆 Champion!',
    '🎯 Bullseye!',
    '💎 Brilliant!',
  ];
  const message = messages[Math.floor(Math.random() * messages.length)];

  return (
    <div className='fixed inset-0 pointer-events-none z-50 overflow-hidden'>
      {/* Animated background overlay */}
      <div className='absolute inset-0 bg-gradient-to-br from-yellow-200/20 via-pink-200/20 to-purple-200/20 animate-pulse' />

      {/* Central message */}
      <div className='absolute inset-0 flex items-center justify-center'>
        <div className='text-center animate-bounce'>
          <div
            className='text-7xl mb-4 animate-spin-slow'
            style={{ animationDuration: '2s' }}
          >
            {message.split(' ')[0]}
          </div>
          <div className='text-3xl font-bold text-gray-800 bg-white/80 px-6 py-3 rounded-full shadow-lg'>
            {message.split(' ').slice(1).join(' ')}
          </div>
        </div>
      </div>

      {/* Floating icons */}
      {icons.map(icon => (
        <div
          key={icon.id}
          className='absolute text-4xl animate-float-up'
          style={{
            left: `${icon.x}%`,
            top: `${icon.y}%`,
            transform: `rotate(${icon.rotation}deg)`,
            animationDelay: `${icon.delay}s`,
            animationDuration: `${2 + Math.random() * 1}s`,
          }}
        >
          {icon.emoji}
        </div>
      ))}

      {/* Confetti-like particles */}
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={`particle-${i}`}
          className='absolute w-2 h-2 rounded-full animate-float-up'
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            backgroundColor: [
              '#fbbf24',
              '#f472b6',
              '#a78bfa',
              '#60a5fa',
              '#34d399',
            ][Math.floor(Math.random() * 5)],
            animationDelay: `${Math.random() * 0.5}s`,
            animationDuration: `${1.5 + Math.random() * 1}s`,
          }}
        />
      ))}
    </div>
  );
};

const ExercisePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAuthToken } = useAuth();
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [content, setContent] = useState<string>('');
  const [steps, setSteps] = useState<Step[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [celebratingStep, setCelebratingStep] = useState<string | null>(null);
  const [confettiActive, setConfettiActive] = useState(false);

  const fetchExercise = useCallback(async () => {
    try {
      const response = await fetch(
        `${getApiUrl()}/api/exercises/${id}`
      );
      const data = await response.json();
      setExercise(data.exercise);

      // Fetch content
      const contentResponse = await fetch(
        `${getApiUrl()}/api/exercises/${id}/content`
      );
      const contentData = await contentResponse.json();

      // Handle both string content (raw markdown) and object content (parsed)
      if (typeof contentData.content === 'string') {
        // Remove HTML comments before processing
        const cleanContent = removeHtmlComments(contentData.content);
        setContent(cleanContent);
        // Extract steps from markdown if it's a string
        const extractedSteps = extractStepsFromMarkdown(cleanContent);
        setSteps(extractedSteps);
      } else if (
        contentData.content &&
        typeof contentData.content === 'object'
      ) {
        // It's a parsed content object
        if (
          contentData.content.steps &&
          Array.isArray(contentData.content.steps)
        ) {
          // Transform: expand numbered items within Exercise step into completable steps
          const { steps: transformedSteps, fullContent } =
            transformApiStepsToCompletableSteps(contentData.content.steps);
          setSteps(transformedSteps);
          setContent(removeHtmlComments(fullContent));
        } else {
          // Object but no steps - try to stringify or use description
          const contentStr =
            contentData.content.description ||
            JSON.stringify(contentData.content, null, 2) ||
            '';
          setContent(removeHtmlComments(contentStr));
          setSteps([]);
        }
      } else {
        // Fallback: empty content
        setContent('');
        setSteps([]);
      }
    } catch (error) {
      console.error('Error fetching exercise:', error);
    }
  }, [id]);

  const fetchUserProgress = useCallback(async () => {
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${getApiUrl()}/api/progress/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      if (data.progress) {
        setUserProgress(data.progress);
        setNotes(data.progress.notes || '');
        setCompletedSteps(data.progress.completed_steps || []);
      }
    } catch (error) {
      console.error('Error fetching user progress:', error);
    } finally {
      setLoading(false);
    }
  }, [id, getAuthToken]);

  useEffect(() => {
    if (id) {
      fetchExercise();
      fetchUserProgress();
    }
  }, [id, fetchExercise, fetchUserProgress]);

  const saveProgress = async () => {
    if (!id) return;

    setSaving(true);
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${getApiUrl()}/api/progress/${id}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: 'in_progress',
            completed_steps: completedSteps,
            notes: notes,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserProgress(data.progress);
      }
    } catch (error) {
      console.error('Error saving progress:', error);
    } finally {
      setSaving(false);
    }
  };

  const markAsCompleted = async () => {
    if (!id) return;

    setSaving(true);
    try {
      const token = await getAuthToken();
      const response = await fetch(
        `${getApiUrl()}/api/progress/${id}/complete`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            completed_steps: completedSteps,
            notes: notes,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserProgress(data.progress);
        // Show success message or redirect
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error marking as completed:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleStep = (step: string) => {
    const isCompleting = !completedSteps.includes(step);
    setCompletedSteps(prev =>
      prev.includes(step) ? prev.filter(s => s !== step) : [...prev, step]
    );

    // Show celebration when completing a step
    if (isCompleting) {
      setCelebratingStep(step);
      setConfettiActive(true);

      // Hide celebration after animation
      setTimeout(() => {
        setCelebratingStep(null);
        setConfettiActive(false);
      }, 3000);
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-hexagon to-hexagon-dark animate-pulse'></div>
          </div>
          <p className='text-gray-600'>Loading exercise...</p>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='w-24 h-24 mx-auto mb-4'>
            <div className='hexagon bg-red-200 flex items-center justify-center'>
              <BookOpen className='w-12 h-12 text-red-500' />
            </div>
          </div>
          <p className='text-gray-600 text-lg'>Exercise not found</p>
          <button
            onClick={() => navigate('/dashboard')}
            className='mt-4 text-hexagon hover:text-hexagon-dark font-medium'
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6 relative'>
      {/* Celebration overlay - full screen */}
      {confettiActive && celebratingStep && <CelebrationOverlay />}

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className='flex items-center justify-between'
      >
        <button
          onClick={() => navigate('/dashboard')}
          className='inline-flex items-center space-x-2 text-gray-600 hover:text-hexagon transition-colors duration-200'
        >
          <ArrowLeft className='w-5 h-5' />
          <span>Back to Dashboard</span>
        </button>

        <div className='flex items-center space-x-4'>
          <button
            onClick={saveProgress}
            disabled={saving}
            className='inline-flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50'
          >
            <Save className='w-4 h-4' />
            <span>{saving ? 'Saving...' : 'Save Progress'}</span>
          </button>

          <button
            onClick={markAsCompleted}
            disabled={saving}
            className='hexagon-button inline-flex items-center space-x-2'
          >
            <CheckCircle className='w-5 h-5' />
            <span>Mark Complete</span>
          </button>
        </div>
      </motion.div>

      {/* Exercise Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className='glass-effect rounded-2xl p-6'
      >
        <div className='flex items-start justify-between mb-6'>
          <div className='flex-1'>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>
              {formatExerciseTitle(exercise.title)}
            </h1>
            <p className='text-gray-600 text-lg mb-4'>{exercise.description}</p>

            <div className='flex items-center gap-4'>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(
                  exercise.difficulty
                )}`}
              >
                {exercise.difficulty}
              </span>
              <span className='flex items-center space-x-1 text-gray-600'>
                <Timer className='w-4 h-4' />
                <span>{exercise.estimated_time} min</span>
              </span>
              <span className='flex items-center space-x-1 text-gray-600'>
                <Tag className='w-4 h-4' />
                <span>{exercise.category}</span>
              </span>
            </div>
          </div>

          <div className='w-20 h-20'>
            {(() => {
              const detectedLang = detectLanguage(
                exercise.title,
                exercise.category,
                exercise.tags
              );
              return detectedLang ? (
                <div className='hexagon bg-white flex items-center justify-center p-3 border-2 border-gray-200'>
                  <LanguageIcon language={detectedLang} size={48} />
                </div>
              ) : (
                <div className='hexagon bg-gradient-to-r from-hexagon to-accent-500 flex items-center justify-center'>
                  <BookOpen className='w-10 h-10 text-white' />
                </div>
              );
            })()}
          </div>
        </div>

        {/* Progress Bar */}
        <div className='mb-6'>
          <div className='flex items-center justify-between mb-2'>
            <span className='text-sm font-medium text-gray-700'>Progress</span>
            <span className='text-sm text-gray-500'>
              {completedSteps.length} / {steps.length || 1} steps completed
            </span>
          </div>
          <div className='w-full bg-gray-200 rounded-full h-2'>
            <div
              className='bg-gradient-to-r from-hexagon to-accent-500 h-2 rounded-full transition-all duration-300'
              style={{
                width: `${Math.max(
                  0,
                  Math.min(
                    100,
                    (completedSteps.length / Math.max(steps.length || 1, 1)) *
                      100
                  )
                )}%`,
              }}
            ></div>
          </div>
        </div>
      </motion.div>

      {/* Content and Progress Sidebar */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        {/* Exercise Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className='lg:col-span-2 space-y-6'
        >
          <div className='glass-effect rounded-2xl p-6'>
            <h2 className='text-xl font-semibold text-gray-900 mb-4'>
              Exercise Content
            </h2>
            <div
              className='prose prose-lg prose-slate max-w-none
              prose-headings:font-bold prose-headings:text-gray-900
              prose-h1:text-3xl prose-h1:mb-4 prose-h1:mt-6
              prose-h2:text-2xl prose-h2:mb-3 prose-h2:mt-5
              prose-h3:text-xl prose-h3:mb-2 prose-h3:mt-4
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-hexagon prose-a:no-underline hover:prose-a:underline
              prose-strong:text-gray-900 prose-strong:font-semibold
              prose-ul:list-disc prose-ul:pl-6 prose-ul:mb-4
              prose-ol:list-decimal prose-ol:pl-6 prose-ol:mb-4
              prose-li:text-gray-700 prose-li:mb-2
              prose-code:text-hexagon prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono
              prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg prose-pre:p-4 prose-pre:overflow-x-auto
              prose-blockquote:border-l-4 prose-blockquote:border-hexagon prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-gray-600
              prose-img:rounded-lg prose-img:shadow-md
              prose-hr:border-gray-300 prose-hr:my-8'
            >
              <ReactMarkdown
                components={{
                  h2({ node, children, ...props }: any) {
                    // H2 headings are section headers (like "Exercise Content")
                    // Don't add checkboxes to these, just render normally
                    return (
                      <h2
                        className='text-2xl font-bold text-gray-900 mt-6 mb-4'
                        {...props}
                      >
                        {children}
                      </h2>
                    );
                  },
                  h3({ node, children, ...props }: any) {
                    // H3 headings within Exercise section are the actual steps
                    const headingText = String(children).replace(/^#+\s*/, '');

                    // Check if this looks like a step (matches "Step X" or just "X" pattern)
                    const stepMatch = headingText.match(
                      /^(?:Step\s+)?(\d+)[.:]?\s*(.*)$/i
                    );

                    if (stepMatch) {
                      const stepNumber = parseInt(stepMatch[1], 10);
                      const stepTitleSuffix = stepMatch[2]?.trim() || '';
                      const stepTitle = stepTitleSuffix
                        ? `Step ${stepNumber}: ${stepTitleSuffix}`
                        : `Step ${stepNumber}`;

                      // Find matching step
                      const step = steps.find(s => {
                        // Match by step number or title
                        const stepNumMatch = s.id.match(/step-(\d+)/);
                        return (
                          (stepNumMatch &&
                            parseInt(stepNumMatch[1], 10) === stepNumber) ||
                          s.title === stepTitle ||
                          s.title.includes(`Step ${stepNumber}`)
                        );
                      });

                      const stepId = step?.id || `step-${stepNumber}`;
                      const isCompleted = completedSteps.includes(stepId);
                      const isCelebrating = celebratingStep === stepId;

                      return (
                        <div className='flex items-start gap-3 mt-6 mb-4 group relative'>
                          <button
                            onClick={() => toggleStep(stepId)}
                            className={`mt-1 flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300 transform ${
                              isCompleted
                                ? 'bg-green-500 border-green-500 text-white scale-110 shadow-lg'
                                : 'border-gray-300 bg-white hover:border-hexagon hover:bg-hexagon-50 hover:scale-105'
                            } ${
                              isCelebrating ? 'animate-pulse scale-125' : ''
                            }`}
                            aria-label={`Mark step "${stepTitle}" as ${
                              isCompleted ? 'incomplete' : 'complete'
                            }`}
                          >
                            {isCompleted && (
                              <CheckCircle className='w-5 h-5 animate-in fade-in zoom-in duration-300' />
                            )}
                            {!isCompleted && (
                              <div className='w-2 h-2 rounded-full bg-gray-400 opacity-0 group-hover:opacity-100 transition-opacity' />
                            )}
                          </button>
                          <h3
                            className={`text-xl font-bold text-gray-900 flex-1 transition-all duration-300 ${
                              isCompleted ? 'text-green-700' : ''
                            } ${isCelebrating ? 'animate-pulse' : ''}`}
                            {...props}
                          >
                            {children}
                            {isCompleted && (
                              <span className='ml-2 text-green-500 animate-in fade-in slide-in-from-right duration-500'>
                                ✓
                              </span>
                            )}
                          </h3>
                          {isCelebrating && (
                            <div className='absolute -top-2 -right-2 text-2xl animate-bounce'>
                              🎉
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Not a step heading, render normally
                    return (
                      <h3
                        className='text-xl font-bold text-gray-900 mt-4 mb-2'
                        {...props}
                      >
                        {children}
                      </h3>
                    );
                  },
                  code({ className, children, ...props }: any) {
                    const match = /language-(\w+)/.exec(className || '');
                    const isInline = !match;
                    return isInline ? (
                      <code
                        className='bg-gray-100 text-hexagon px-1.5 py-0.5 rounded text-sm font-mono'
                        {...props}
                      >
                        {children}
                      </code>
                    ) : (
                      <pre className='bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto mb-4'>
                        <code className={className} {...props}>
                          {children}
                        </code>
                      </pre>
                    );
                  },
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Notes Section */}
          <div className='glass-effect rounded-2xl p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Your Notes
            </h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder='Add your notes, thoughts, or questions here...'
              className='w-full h-32 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent resize-none'
            />
          </div>
        </motion.div>

        {/* Progress Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className='space-y-6'
        >
          {/* Progress Tracking */}
          <div className='glass-effect rounded-2xl p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Track Your Progress
            </h3>

            <div className='space-y-3'>
              {steps.length > 0 ? (
                steps.map(step => {
                  const isCompleted = completedSteps.includes(step.id);
                  return (
                    <button
                      key={step.id}
                      onClick={() => toggleStep(step.id)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg border transition-all duration-200 ${
                        isCompleted
                          ? 'border-green-200 bg-green-50 text-green-800'
                          : 'border-gray-200 bg-white hover:border-hexagon hover:bg-hexagon-50'
                      }`}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          isCompleted ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle className='w-4 h-4 text-white' />
                        ) : (
                          <span className='text-xs font-medium text-gray-600'>
                            {step.order}
                          </span>
                        )}
                      </div>
                      <span className='text-sm font-medium text-left flex-1'>
                        {isCompleted ? '✓ ' : ''}
                        {step.title}
                      </span>
                    </button>
                  );
                })
              ) : (
                <p className='text-sm text-gray-500 text-center py-4'>
                  No steps found in this exercise
                </p>
              )}
            </div>
          </div>

          {/* Exercise Stats */}
          <div className='glass-effect rounded-2xl p-6'>
            <h3 className='text-lg font-semibold text-gray-900 mb-4'>
              Exercise Stats
            </h3>

            <div className='space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-gray-600'>Status</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    userProgress?.status === 'completed'
                      ? 'bg-green-100 text-green-800'
                      : userProgress?.status === 'in_progress'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {userProgress?.status === 'completed'
                    ? 'Completed'
                    : userProgress?.status === 'in_progress'
                    ? 'In Progress'
                    : 'Not Started'}
                </span>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-gray-600'>Steps Completed</span>
                <span className='font-medium'>
                  {completedSteps.length}/{steps.length || 1}
                </span>
              </div>

              <div className='flex items-center justify-between'>
                <span className='text-gray-600'>Estimated Time</span>
                <span className='font-medium'>
                  {exercise.estimated_time} min
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ExercisePage;
